/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83): the call controls of the final design, on Element
 * Call's own buttons and actions.
 *   audio call of two (08·04): Mute · Speaker · Video / Add · Screen · Chat,
 *     end call below; labels under the buttons
 *   video call of two (08·05): video · screen · mic · speaker · end call
 *   group call (08·06): hand · screen · video · mic · end call
 *   outgoing call of two, while it rings (08·03): Microphone · Speaker ·
 *     Video with labels, "Cancel" (= end call) below
 * A button whose action is missing is left out ("Add", "Chat" without the
 * app's support, "Screen" where screen sharing is impossible — see
 * ION_SCREEN_UNAVAILABLE).
 */

import { type FC, type JSX, type ReactNode, type Ref } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { Button as CpdButton, Tooltip } from "@vector-im/compound-web";
import {
  ChatIcon,
  RaisedHandSolidIcon,
  ShareScreenIcon,
  UserAddIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";

import {
  EndCallButton,
  LoudspeakerButton,
  MicButton,
  ReactionToggleButton,
  ShareScreenButton,
  VideoButton,
} from "../button";
import { type FooterSnapshot } from "../components/CallFooter";
import footerStyles from "../components/CallFooter.module.css";
import { type ViewModel } from "../state/ViewModel";
import { useBehavior } from "../useBehavior";
import { constant } from "../state/Behavior";
import { useReactionsSender } from "../reactions/useReactionsSender";
import { pick } from "./callErrorTexts";
import { useIonHostActions } from "./hostActions";
import {
  CALL_SCREEN_TEXTS as T,
  ION_REACTIONS,
  ION_SCREEN_UNAVAILABLE,
} from "./callScreen";
import styles from "./IonCallControls.module.css";

export type IonCallMode = "ringing" | "audio" | "video" | "group";

/**
 * Which controls: a group call, else ringing while the other side has not
 * answered, else video or audio by the own camera.
 */
export function callMode(
  participants: number,
  videoEnabled: boolean,
  ringing = false,
): IonCallMode {
  if (participants > 2) return "group";
  if (ringing) return "ringing";
  return videoEnabled ? "video" : "audio";
}

interface Props {
  vm: ViewModel<FooterSnapshot>;
  roomId: string;
  participants: number;
  /** An outgoing call that rings, not answered yet (08·03). */
  ringing?: boolean;
  className?: string;
  ref?: Ref<HTMLDivElement>;
}

const Item: FC<{ label: string | null; children: ReactNode }> = ({
  label,
  children,
}) => (
  <div className={styles.item}>
    {children}
    {label !== null && (
      <span className={styles.label} data-testid="ion-control-label">
        {label}
      </span>
    )}
  </div>
);

export const IonCallControls: FC<Props> = ({
  vm,
  roomId,
  participants,
  ringing = false,
  className,
  ref,
}) => {
  const { i18n } = useTranslation();
  const l = i18n.language;
  const host = useIonHostActions();
  const { toggleRaisedHand } = useReactionsSender();

  const showFooter = useBehavior(vm.showFooter$);
  const asOverlay = useBehavior(vm.asOverlay$);
  const hideControls = useBehavior(vm.hideControls$);
  const showModals = useBehavior(vm.showModals$);
  const audioEnabled = useBehavior(vm.audioEnabled$) ?? false;
  const audioBusy = useBehavior(vm.audioBusy$) ?? false;
  const videoEnabled = useBehavior(vm.videoEnabled$) ?? false;
  const videoBusy = useBehavior(vm.videoBusy$) ?? false;
  const toggleAudio = useBehavior(vm.toggleAudio$);
  const toggleVideo = useBehavior(vm.toggleVideo$);
  const sharingScreen = useBehavior(vm.sharingScreen$) ?? false;
  const toggleScreenSharing = useBehavior(vm.toggleScreenSharing$);
  const audioOutputSwitcher = useBehavior(vm.audioOutputSwitcher$);
  const reactionIdentifier = useBehavior(vm.reactionIdentifier$);
  const reactionData = useBehavior(vm.reactionData$);
  const hangup = useBehavior(vm.hangup$);
  const size = useBehavior(vm.buttonSize$);
  const handsRaised = useBehavior(
    reactionData?.handsRaised$ ?? NO_HANDS_RAISED$,
  );

  const mode = callMode(participants, videoEnabled, ringing);
  const labelled = mode === "audio" || mode === "ringing";
  const label = (text: { ru: string; en: string }): string | null =>
    labelled ? pick(text, l) : null;

  const mic = (
    <Item
      key="mic"
      label={label(
        mode === "ringing" ? T.microphone : audioEnabled ? T.mute : T.unmute,
      )}
    >
      <MicButton
        size={size}
        enabled={audioEnabled}
        busy={audioBusy}
        onClick={toggleAudio}
        disabled={audioBusy || toggleAudio === undefined}
        data-testid="incall_mute"
      />
    </Item>
  );
  const speaker = audioOutputSwitcher && (
    <Item key="speaker" label={label(T.speaker)}>
      <LoudspeakerButton
        size={size}
        onClick={() => audioOutputSwitcher.switch()}
        loudspeakerModeEnabled={audioOutputSwitcher.targetOutput === "earpiece"}
      />
    </Item>
  );
  const video = (
    <Item key="video" label={label(T.video)}>
      <VideoButton
        size={size}
        enabled={videoEnabled}
        busy={videoBusy}
        onClick={toggleVideo}
        disabled={videoBusy || toggleVideo === undefined}
        data-testid="incall_videomute"
      />
    </Item>
  );
  const screen =
    toggleScreenSharing !== undefined ? (
      <Item key="screen" label={label(T.screen)}>
        <ShareScreenButton
          size={size}
          enabled={sharingScreen}
          onClick={toggleScreenSharing}
          data-testid="incall_screenshare"
        />
      </Item>
    ) : ION_SCREEN_UNAVAILABLE === "disabled" ? (
      <Item key="screen" label={label(T.screen)}>
        <Tooltip label={pick(T.screenUnavailable, l)}>
          <CpdButton
            iconOnly
            kind="secondary"
            Icon={ShareScreenIcon}
            aria-disabled
            aria-label={pick(T.screenUnavailable, l)}
          />
        </Tooltip>
      </Item>
    ) : null;
  const add = host.can("add_people") && (
    <Item key="add" label={label(T.add)}>
      <Tooltip label={pick(T.add, l)}>
        <CpdButton
          iconOnly
          kind="secondary"
          Icon={UserAddIcon}
          aria-label={pick(T.add, l)}
          onClick={() => host.send("add_people", { room_id: roomId })}
        />
      </Tooltip>
    </Item>
  );
  const chat = host.can("open_chat") && (
    <Item key="chat" label={label(T.chat)}>
      <Tooltip label={pick(T.chat, l)}>
        <CpdButton
          iconOnly
          kind="secondary"
          Icon={ChatIcon}
          aria-label={pick(T.chat, l)}
          onClick={() => host.send("open_chat", { room_id: roomId })}
        />
      </Tooltip>
    </Item>
  );
  const raised =
    reactionIdentifier !== undefined && !!handsRaised[reactionIdentifier];
  const hand = mode === "group" && reactionIdentifier !== undefined && (
    <Item key="hand" label={null}>
      <Tooltip label={pick(T.hand, l)}>
        <CpdButton
          iconOnly
          kind={raised ? "primary" : "secondary"}
          Icon={RaisedHandSolidIcon}
          aria-label={pick(T.hand, l)}
          aria-pressed={raised}
          onClick={() => void toggleRaisedHand()}
        />
      </Tooltip>
    </Item>
  );
  const reactions = ION_REACTIONS === "button" &&
    reactionIdentifier &&
    reactionData &&
    showModals && (
      <Item key="reactions" label={label(T.reactions)}>
        <ReactionToggleButton
          size={size}
          reactionData={reactionData}
          identifier={reactionIdentifier}
        />
      </Item>
    );
  const end = hangup && (
    <Item key="end" label={label(mode === "ringing" ? T.cancel : T.endCall)}>
      <EndCallButton size={size} onClick={hangup} data-testid="incall_leave" />
    </Item>
  );

  const keep = (items: (JSX.Element | false | null | undefined | "")[]) =>
    items.filter((i): i is JSX.Element => !!i);

  let body: ReactNode;
  if (mode === "ringing") {
    body = (
      <>
        <div className={styles.grid}>{keep([mic, speaker, video])}</div>
        <div className={styles.end}>{end}</div>
      </>
    );
  } else if (mode === "audio") {
    body = (
      <>
        <div className={styles.grid}>
          {keep([mic, speaker, video, add, screen, chat, reactions])}
        </div>
        <div className={styles.end}>{end}</div>
      </>
    );
  } else if (mode === "video") {
    body = (
      <div className={styles.row}>
        {keep([video, screen, mic, speaker, reactions, end])}
      </div>
    );
  } else {
    body = (
      <div className={styles.row}>
        {keep([hand, screen, video, mic, reactions, end])}
      </div>
    );
  }

  return (
    <div
      ref={ref}
      data-testid="footer-container"
      data-ion-mode={mode}
      className={classNames(className, footerStyles.footer, styles.controls, {
        [footerStyles.overlay]: asOverlay,
        [footerStyles.hidden]: !showFooter,
      })}
    >
      {!hideControls && body}
    </div>
  );
};

const NO_HANDS_RAISED$ = constant<Record<string, unknown>>({});
